import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Header, 
  SpaceBetween, 
  Grid, 
  Cards,
  StatusIndicator, 
  ColumnLayout, 
  BarChart,
  BarChartProps,
  Button,
  Spinner
} from '@cloudscape-design/components';
import { useAuth } from '../contexts/AuthContext';
import { useHelpPanel } from '../contexts/HelpContext';
import { InfoLink } from '../components/help/common';
import { DashboardHelp } from '../components/help/DashboardHelp';
import { eventApi } from '../services/api';

// Dashboard component props
interface DashboardProps {
  addNotification: (notification: any) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ addNotification }) => {
  const { isAdmin } = useAuth();
  const { setContent } = useHelpPanel();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProcessorLoading, setIsProcessorLoading] = useState<boolean>(false);
  const [isEscalationLoading, setIsEscalationLoading] = useState<boolean>(false);

  // Simulated loading effect - in a real app, this would be an API call
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Handler for running the event processor
  const handleRunEventProcessor = async () => {
    setIsProcessorLoading(true);
    try {
      const response = await eventApi.runProcessor();
      addNotification({
        type: 'success',
        content: 'Event processor executed successfully',
        dismissible: true
      });
    } catch (error) {
      console.error('Error running event processor:', error);
      addNotification({
        type: 'error',
        content: 'Failed to run event processor',
        dismissible: true
      });
    } finally {
      setIsProcessorLoading(false);
    }
  };

  // Handler for running the event escalation
  const handleRunEventEscalation = async () => {
    setIsEscalationLoading(true);
    try {
      const response = await eventApi.runEscalation();
      addNotification({
        type: 'success',
        content: 'Event escalation executed successfully',
        dismissible: true
      });
    } catch (error) {
      console.error('Error running event escalation:', error);
      addNotification({
        type: 'error',
        content: 'Failed to run event escalation',
        dismissible: true
      });
    } finally {
      setIsEscalationLoading(false);
    }
  };

  // Sample chart data
  const notificationChartData = [
    {
      title: 'Notifications Sent',
      type: 'bar' as const,
      data: [
        { x: 'New Deployment', y: 56 },
        { x: 'Multiple Solutions', y: 38 },
        { x: 'Repeat Deployment', y: 27 },
        { x: 'Long Term', y: 15 },
      ],
    },
  ];

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <Header
          variant="h1"
          info={<InfoLink onFollow={() => setContent(<DashboardHelp />)} />}
          actions={
            isAdmin && (
              <SpaceBetween size="xs" direction="horizontal">
                <Button 
                  iconName="external" 
                  variant="primary"
                  loading={isProcessorLoading}
                  onClick={handleRunEventProcessor}
                >
                  Run Event Processor
                </Button>
                <Button 
                  iconName="notification" 
                  variant="normal"
                  loading={isEscalationLoading}
                  onClick={handleRunEventEscalation}
                >
                  Run Event Escalation
                </Button>
              </SpaceBetween>
            )
          }
        >
          Dashboard
        </Header>

        {isLoading ? (
          <Container>
            <Box textAlign="center" padding="xxl">
              <Spinner size="large" />
              <Box variant="p" padding={{ top: 'l' }}>
                Loading dashboard data...
              </Box>
            </Box>
          </Container>
        ) : (
          <>
            {/* Status Cards */}
            <Grid
              gridDefinition={[
                { colspan: { default: 12, xs: 6, m: 3 } },
                { colspan: { default: 12, xs: 6, m: 3 } },
                { colspan: { default: 12, xs: 6, m: 3 } },
                { colspan: { default: 12, xs: 6, m: 3 } },
              ]}
            >
              <Container>
                <SpaceBetween size="m">
                  <Box variant="h2">Emails Sent</Box>
                  <Box variant="h1" textAlign="center" padding="s">
                    136
                  </Box>
                  <StatusIndicator type="success">Active</StatusIndicator>
                </SpaceBetween>
              </Container>

              <Container>
                <SpaceBetween size="m">
                  <Box variant="h2">Open Rate</Box>
                  <Box variant="h1" textAlign="center" padding="s">
                    68%
                  </Box>
                  <StatusIndicator type="info">Tracking</StatusIndicator>
                </SpaceBetween>
              </Container>

              <Container>
                <SpaceBetween size="m">
                  <Box variant="h2">Slack Notifications</Box>
                  <Box variant="h1" textAlign="center" padding="s">
                    43
                  </Box>
                  <StatusIndicator type="warning">Attention</StatusIndicator>
                </SpaceBetween>
              </Container>

              <Container>
                <SpaceBetween size="m">
                  <Box variant="h2">Pending Events</Box>
                  <Box variant="h1" textAlign="center" padding="s">
                    12
                  </Box>
                  <StatusIndicator type="info">Processing</StatusIndicator>
                </SpaceBetween>
              </Container>
            </Grid>

            {/* Charts Section */}
            <Grid
              gridDefinition={[
                { colspan: 12 } // Chart takes full width (12/12 columns)
              ]}
            >
              <Container
                header={
                  <Header>Notifications by Type (Past 30 Days)</Header>
                }
              >
                <BarChart
                  series={notificationChartData}
                  height={300}
                  xDomain={['New Deployment', 'Multiple Solutions', 'Repeat Deployment', 'Long Term']}
                  yDomain={[0, 60]}
                  horizontalBars
                  hideLegend
                  hideFilter
                />
              </Container>
            </Grid>

            {/* Recent Activity */}
            <Container header={<Header>Recent Activity</Header>}>
              <Cards
                cardDefinition={{
                  header: item => item.title,
                  sections: [
                    {
                      id: 'description',
                      content: item => item.description,
                    },
                    {
                      id: 'time',
                      header: 'Time',
                      content: item => item.time,
                    },
                    {
                      id: 'type',
                      header: 'Type',
                      content: item => (
                        <StatusIndicator type={item.statusType}>{item.type}</StatusIndicator>
                      ),
                    },
                  ],
                }}
                cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
                items={[
                  {
                    title: 'Email sent to account manager',
                    description:
                      'Email notification for New Deployment was sent to john.doe@example.com',
                    time: '10 minutes ago',
                    type: 'Email',
                    statusType: 'success' as const,
                  },
                  {
                    title: 'Slack notification escalated',
                    description:
                      'Slack notification was sent to @sarah regarding unread email',
                    time: '1 hour ago',
                    type: 'Slack',
                    statusType: 'info' as const,
                  },
                  {
                    title: 'New deployment detected',
                    description:
                      'Acme Corp deployed Solution X for the first time',
                    time: '2 hours ago',
                    type: 'Event',
                    statusType: 'success' as const,
                  },
                  {
                    title: 'Email tracking event',
                    description: 'Email to mike.jones@example.com was opened',
                    time: 'Yesterday',
                    type: 'Tracking',
                    statusType: 'info' as const,
                  },
                ]}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No recent activity</b>
                    <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                      No activity has been recorded recently.
                    </Box>
                  </Box>
                }
              />
            </Container>
          </>
        )}
      </SpaceBetween>
    </Box>
  );
};

export default Dashboard;
